import { ST } from '../state.js';

// Dependencies: buildFactBlock() from js/prompts/existing.js (runtime only)

/**
 * Match a concept section to its siteStructure entry.
 * @param {object} section
 * @returns {object | null}
 */
function matchSiteStructure(section) {
  const ss = ST.marketingBrief?.siteStructure || [];
  if (!ss.length) {
    return null;
  }
  // Direct lookup by _structOrder (set in keep mode)
  if (section._structOrder != null) {
    const entry = ss.find(s => s.order === section._structOrder);
    if (entry) {
      return entry;
    }
  }
  // Fallback: match by type (refresh mode doesn't have _structOrder)
  return ss.find(s => s.type === section.type) || null;
}

/**
 * Section-level mode prompt for existing website builds.
 * @param {'keep' | 'refresh' | 'rebrand'} opt - Content strategy mode
 * @param {object} s - Section config from the concept
 * @param {object} scrape - Scraped site data
 * @returns {string}
 */
function getVideoBlock(structEntry) {
  if (!structEntry?.video?.url) return '';
  const v = structEntry.video;
  return `\n\n<section_video provider="${v.provider}" url="${v.url}"${v.posterUrl ? ` poster="${v.posterUrl}"` : ''}>Include this video in the section using the appropriate embed method.</section_video>`;
}

function getSectionModePrompt(opt, s, scrape) {
  // Facts are already in the marketing brief context — no separate factBlock injection needed
  if (opt === 'keep') {
    const structEntry = matchSiteStructure(s);
    let contentBlock = '';
    let imageBlock = '';
    let videoBlock = '';
    if (structEntry) {
      contentBlock = structEntry.content || '';
      videoBlock = getVideoBlock(structEntry);
      if (structEntry.images?.length) {
        imageBlock =
          '\n\n<existing_images>\n' +
          structEntry.images
            .map(img => {
              const proxied = img.url?.startsWith('http')
                ? `/api/img-proxy?src=${encodeURIComponent(img.url)}`
                : img.url;
              return `- ${proxied} (${img.position || 'unknown position'}: ${img.description || ''})`;
            })
            .join('\n') +
          '\n</existing_images>';
      }
    }
    return `<content_lock>
The following copy is from the user's existing website. Reproduce this text verbatim in your HTML output — preserve original phrasing, grammar, and punctuation. Your job is to present it with excellent visual design using the new design tokens.
${
  contentBlock
    ? `
<existing_copy>
${contentBlock}
</existing_copy>`
    : ''
}${imageBlock}${videoBlock}

<fact_integrity>
- Phone numbers: preserve the exact format as extracted (users recognize their own number)
- Credentials: preserve exact spelling and capitalization (e.g. "BBB A+ Rating" not "BBB A rating")
- Business name: use consistently throughout
- Stats: preserve exact figure format ("10+" not "ten", "500K" not "500,000")
- Use only facts present in the copy above and the marketing brief's fact_inventory.
</fact_integrity>

<image_strategy>Reuse all existing image URLs listed above.</image_strategy>

<self_check>Verify: all original content reproduced verbatim, section plan matches original structure.</self_check>
</content_lock>`;
  }

  if (opt === 'refresh') {
    // Try siteStructure match for richer original content
    const structEntry = matchSiteStructure(s);
    let origContent = '';
    let origImages = '';
    const videoBlock = getVideoBlock(structEntry);
    if (structEntry) {
      origContent = structEntry.content || '';
      if (structEntry.images?.length) {
        origImages =
          '\n\nOriginal images (prefer reuse, replace if low quality):\n' +
          structEntry.images
            .map(img => {
              const proxied = img.url?.startsWith('http')
                ? `/api/img-proxy?src=${encodeURIComponent(img.url)}`
                : img.url;
              return `- ${proxied} (${img.position || ''}: ${img.description || ''})`;
            })
            .join('\n');
      }
    }
    const brief = ST.copywritingBrief;
    return `<content_refresh>
Rewrite all copy in this section for better conversion. The original copy is provided as reference for facts and intent — write fresh, not from the template.

<rewrite_rules>
- Headlines: rewrite to be specific, benefit-led, and urgent — use specific action verbs. "Our Services" → "Garage Door Solutions That Last"
- Body: shorter, scannable, action-oriented. Cut filler. Every sentence should earn its place.
- CTAs: use specific action verbs tied to the offer. "Contact Us" → "Get a Free Quote Today"${
      brief?.voiceSummary ? `\n- Voice: ${brief.voiceSummary}` : ''
    }${brief?.keyMessages?.length ? `\n- Key messages to weave in: ${brief.keyMessages.join('; ')}` : ''}
</rewrite_rules>

${
  origContent
    ? `
<original_copy>
${origContent}
</original_copy>`
    : ''
}${origImages}${videoBlock}

<fact_integrity>
Facts from the brief should appear in their original form:
- Phone numbers, credentials, service areas, named staff — use the exact extracted values.
- Use only facts present in the brief. If a section needs a proof point that doesn't exist, use a structural placeholder like "[X] years in business" for user completion.
</fact_integrity>

<image_strategy>Reuse existing images. Flag low-quality or irrelevant ones for replacement.</image_strategy>

<self_check>Verify: all facts from inventory present, rewritten copy is specific and conversion-focused.</self_check>
</content_refresh>`;
  }

  // rebrand
  const structEntry = matchSiteStructure(s);
  const videoBlock = getVideoBlock(structEntry);
  const c = ST.concept;
  return `<full_rebrand>
Write all copy from scratch, independently of the old website's language, tone, or structure. The copy should feel as if this brand was born this way.

<brand_direction>
Positioning: ${c?.strategy || ''}
Voice: ${c?.brandVoiceSample || ''}
Visual direction: ${c?.designVision?.concept || ''}
</brand_direction>

<fact_constraints>
Use facts from the marketing brief's fact_inventory as the source of truth — verbatim where relevant.
If a fact is missing (e.g. no year of founding), write confident copy that doesn't reference it.
</fact_constraints>

<image_strategy>Prefer stock photos and CSS visuals over old site images. Reuse existing images only if clearly high-quality (professional photography, logo).</image_strategy>${videoBlock}

<self_check>Verify: new brand voice is consistent, all facts from inventory accurately used.</self_check>
</full_rebrand>`;
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export { getSectionModePrompt, matchSiteStructure };
